import React, {useState} from "react";
import {
  AppBar as MuiAppBar,
  Toolbar,
  Typography,
  IconButton,
  Box,
  MenuItem,
  Select,
  Menu,
  Divider,
  ListItemIcon,
  useTheme,
  useMediaQuery,
  Badge,
} from "@mui/material";
import {useTranslation} from "react-i18next";
import AccountCircle from "@mui/icons-material/AccountCircle";
import LogoutIcon from "@mui/icons-material/Logout";
import MenuIcon from "@mui/icons-material/Menu";
import NotificationsIcon from "@mui/icons-material/Notifications";
import {useTenantTheme} from "../../themes/TenantThemeProvider";
import {tenantThemes} from "../../themes/tenantThemes";
import LanguageSelector from "./LanguageSelector";
import {useAuth} from "../../contexts/AuthContext";
import {UserRole} from "../../api/user";

interface AppBarProps {
  handleDrawerToggle: () => void;
  onNotificationClick: () => void;
  unreadCount?: number;
}

const AppBar: React.FC<AppBarProps> = ({handleDrawerToggle, onNotificationClick, unreadCount = 0}) => {
  const {t} = useTranslation();
  const {tenant, setTenant, themeConfig, tenantLogo} = useTenantTheme();
  const {user, logout} = useAuth();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Use tenant logo if available, otherwise use theme logo
  const logoUrl = tenantLogo || themeConfig.logo;

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const handleLogout = () => {
    handleClose();
    logout();
  };

  return (
    <MuiAppBar position="static" color="default" elevation={1} sx={{zIndex: 1200}}>
      <Toolbar sx={{py: 1}}>
        {isMobile && (
          <IconButton color="inherit" aria-label="open drawer" edge="start" onClick={handleDrawerToggle} sx={{mr: 2}}>
            <MenuIcon />
          </IconButton>
        )}
        <Box sx={{flexGrow: 1}}>
          <img src={logoUrl} alt={`${themeConfig.name} Logo`} style={{height: "60px", verticalAlign: "middle"}} />
        </Box>
        <Box display="flex" alignItems="center" gap={2}>
          {/* Tenant Switcher - Only show for super_admin */}
          {user && user.role === UserRole.SUPER_ADMIN && (
            <Select size="small" value={tenant} onChange={(e) => setTenant(e.target.value)} sx={{minWidth: 120, display: {xs: "none", md: "flex"}}}>
              {Object.entries(tenantThemes).map(([themeName, theme]) => (
                <MenuItem key={themeName} value={themeName}>
                  {theme.name}
                </MenuItem>
              ))}
            </Select>
          )}
          {/* Language Selector */}
          <Box sx={{display: {xs: "none", md: "flex"}}}>
            <LanguageSelector />
          </Box>
          {/* Notification Bell */}
          <IconButton color="inherit" onClick={onNotificationClick}>
            <Badge badgeContent={unreadCount} color="error">
              <NotificationsIcon />
            </Badge>
          </IconButton>
          <IconButton color="inherit" onClick={handleMenu}>
            <AccountCircle />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            onClick={handleClose}
            transformOrigin={{horizontal: "right", vertical: "top"}}
            anchorOrigin={{horizontal: "right", vertical: "bottom"}}>
            {user && (
              <Box px={2} py={1}>
                <Typography variant="subtitle2">{user.name || user.email}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {user.email}
                </Typography>
              </Box>
            )}
            <Divider />
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              {t("Logout")}
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
    </MuiAppBar>
  );
};

export default AppBar;
